import {
  Box,
  Grid,
  Stack,
  styled,
  useMediaQuery,
  useTheme,
  Button,
  IconButton
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mousewheel, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import Lightbox from 'yet-another-react-lightbox'
import FancyInput from '../../components/FancyInput'
import StepsBtn from '../../components/StepsBtn'
import TextInformation from '../../components/TextInformation'
import TotalToPay from '../../components/TotalToPay'
import '../../i18n'
import CountrySelector from '../../utils/countrySelector/CountrySelector'
import { formatPhoneNumber, isValidEmail } from '../../utils/inputsFormat'
import { useCountries } from '../../queries/useUtilQueries'
import Progress from '../../components/Progress'
import { setFormDataKey, useFormData } from '../../queries/userGlobalQueries'
import PlacesAutocomplete from '../../components/PlacesAutocomplete'
import { queryClient } from '../../queries/config'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { FormDataModel } from '../../utils/data'

const LightboxStyled = styled(Lightbox)(() => ({
  '& .yarl__container': {
    backgroundColor: '#000000b0'
  },
  '& img': {
    width: '600px !important',
    height: '400px !important',
    maxWidth: '600px !important',
    maxHeight: '400px !important'
  }
}))

interface CountryOption {
  id: number
  name: string
  areaCode: string
  flag: string | null
  userCreated: string
  userModified: string
}

const BoxStyled = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  alignItems: 'center',
  maxWidth: '700px',
  width: '100%',
  marginTop: 30,
  rowGap: theme.spacing(4),
  columnGap: theme.spacing(4),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr'
  }
}))

const UserData = ({
  handleBack,
  handleNext,
  colegial,
  cartData,
  setActiveStep,
  setHiddenCart,
  setCartData,
  currency,
  areasData
}: any) => {
  const { t } = useTranslation()
  const { data: formData } = useFormData()
  const theme = useTheme()
  const isMd = useMediaQuery(theme.breakpoints.down('md'))
  const isXl = useMediaQuery(theme.breakpoints.up('xl'))
  const [areaCodeInput, setAreaCodeInput] = useState<CountryOption | null>(null)

  const [phoneInput, setPhoneInput] = useState(formData.userPhone ? formData.userPhone : '')
  const [emailInput, setEmailInput] = useState(formData.userEmail ? formData.userEmail : '')
  const [pickUp, setPickUp] = useState(formData.pickUp ? formData.pickUp : '')
  const [dropOff, setDropOff] = useState(formData.dropOff ? formData.dropOff : '')

  const [urlImg, setUrlImg] = useState<string[] | undefined>(
    formData.photoGallery ? formData.photoGallery : undefined
  )
  const [open, setOpenLightbox] = useState<boolean>(false)
  const [indexImg, setIndexImg] = useState<number>(0)

  const type = formData.areaType

  // Query para países
  const { data: countryData, isLoading } = useCountries(0)

  useEffect(() => {
    document.documentElement.scrollTop = 0
  }, [])

  const disabledNextBtn = !formData.isColegial
    ? !formData.userName ||
      !formData.userLastName ||
      !isValidEmail(formData.userEmail) ||
      !phoneInput ||
      formData.userNroId.length > 15 ||
      formData.userNroId.length === 0 ||
      phoneInput.length > 15 ||
      phoneInput.length === 0 ||
      !areaCodeInput ||
      !formData.pickUp ||
      !formData.dropOff
    : false

  const handlePhoneInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const formattedPhoneNumber = formatPhoneNumber(event.target.value)
    setPhoneInput(formattedPhoneNumber)

    queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
      ...old,
      userPhone: event.target.value
    }))
  }

  const backBtn = () => {
    setActiveStep(0)
    setHiddenCart(false)
  }

  const backFromUser = () => {
    const oldData = cartData.allBookings
    oldData.splice(-cartData.lastItems)
    setCartData({
      allAmount: cartData.allAmount - cartData.lastAmount,
      allBookings: oldData
    })
    handleBack()
  }

  const handleLightbox = (index: number) => {
    setOpenLightbox(true)
    setIndexImg(index)
  }

  const urlImgs = urlImg?.map((img) => ({
    src: img
  }))

  if (isLoading) {
    return <Progress />
  }

  return (
    <>
      <Grid container columnSpacing={4} sx={{ height: '100%' }}>
        <Grid
          item
          xs={12}
          lg={8}
          sx={{
            borderRight: {
              lg: '1px solid'
            },
            pr: 3,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Stack>
            {(isMd || isXl) && <TextInformation title={`${t('userInformation')}`} />}
            {!formData.isColegial ? (
              <BoxStyled>
                <FancyInput
                  required={true}
                  value={formData.userName}
                  onChange={(event) => {
                    const inputValue = event.target.value
                    const filteredValue = inputValue.replace(/[^A-Za-z\s]/g, '')

                    queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                      ...old,
                      userName: filteredValue
                    }))
                  }}
                  label={t('firstName')}
                />
                <FancyInput
                  required={true}
                  value={formData.userLastName}
                  onChange={(event) => {
                    const inputValue = event.target.value
                    const filteredValue = inputValue.replace(/[^A-Za-z\s]/g, '')
                    queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                      ...old,
                      userLastName: filteredValue
                    }))
                  }}
                  label={t('lastName')}
                />
                <FancyInput
                  required={true}
                  value={formData.userNroId}
                  type={'text'}
                  label={t('identificationNumber')}
                  inputProps={{
                    maxLength: 15,
                    pattern: '[0-9]*'
                  }}
                  validation={[
                    {
                      validate: () => /^[0-9]*$/.test(formData.userNroId),
                      msg: 'Solo se permiten caracteres numéricos'
                    }
                  ]}
                  onChange={(event) => {
                    const value = event.target.value
                    const filteredValue = value.replace(/[^0-9]/g, '')
                    queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                      ...old,
                      userNroId: filteredValue
                    }))
                  }}
                />
                <FancyInput
                  value={emailInput}
                  label={t('email')}
                  onChange={(event) => {
                    setEmailInput(event.target.value)
                    queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                      ...old,
                      userEmail: event.target.value
                    }))
                  }}
                  required={true}
                  validation={[
                    {
                      validate: () => isValidEmail(formData.userEmail),
                      msg: 'Correo invalido'
                    }
                  ]}
                />
                <Box display="flex">
                  <CountrySelector
                    label="Selecciona tu país"
                    value={formData.countrySelected}
                    disabled={false}
                    selectedCountry={areaCodeInput}
                    setSelectedCountry={(country) => {
                      queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                        ...old,
                        countrySelected: country?.id,
                        country: country?.id
                      }))
                      setAreaCodeInput(country)
                    }}
                    options={countryData?.body}
                  />
                </Box>
                <FancyInput
                  required={true}
                  value={phoneInput}
                  onChange={handlePhoneInput}
                  label={t('phone')}
                  inputProps={{
                    maxLength: 16
                  }}
                  validation={[
                    {
                      validate: () => phoneInput.length < 15,
                      msg: `${t('noMoreFifteenChar')}`
                    }
                  ]}
                />
                {type === 'transportation' && (
                  <>
                    <PlacesAutocomplete
                      required={type === 'transportation'}
                      value={pickUp}
                      onChange={(value, placeId) => {
                        setPickUp(value)
                        queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                          ...old,
                          pickUp: value,
                          pickUpId: placeId
                        }))
                      }}
                      label={t('pickUp')}
                      helperText={t('pickUpLocation')}
                    />
                    <PlacesAutocomplete
                      required={type === 'transportation'}
                      value={dropOff}
                      onChange={(value, placeId) => {
                        setDropOff(value)
                        queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                          ...old,
                          dropOff: value,
                          dropOffId: placeId
                        }))
                      }}
                      label={t('dropOff')}
                      helperText={t('dropOffLocation')}
                    />
                    <Box
                      sx={{
                        gridColumn: '1 / -1',
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 2
                      }}
                    >
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                          queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                            ...old,
                            stops: [...(old.stops || []), { location: '', placeId: '' }]
                          }))
                        }}
                        variant="outlined"
                        size="small"
                      >
                        Agregar parada
                      </Button>
                    </Box>
                    {formData.stops &&
                      formData.stops.map((stop, index) => (
                        <Box
                          key={index}
                          sx={{
                            gridColumn: '1 / -1',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'end',
                            gap: 1
                          }}
                        >
                          <PlacesAutocomplete
                            required={false}
                            value={stop.location}
                            onChange={(value, placeId) => {
                              queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                                ...old,
                                stops: old.stops.map((s, i) =>
                                  i === index ? { location: value, placeId: placeId || '' } : s
                                )
                              }))
                            }}
                            label={`Parada ${index + 1}`}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <IconButton
                              onClick={() => {
                                queryClient.setQueryData(
                                  [setFormDataKey],
                                  (old: FormDataModel) => ({
                                    ...old,
                                    stops: old.stops.filter((_, i) => i !== index)
                                  })
                                )
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                  </>
                )}
              </BoxStyled>
            ) : (
              <BoxStyled>
                <FancyInput disabled value={colegial.name} label={t('name')} onChange={undefined} />
                <FancyInput
                  disabled
                  value={colegial.phone}
                  label={t('phone')}
                  onChange={undefined}
                />
                <FancyInput
                  disabled
                  value={colegial.email}
                  label={t('email')}
                  onChange={undefined}
                />
                <Box display="flex" width="100%">
                  <CountrySelector
                    value={colegial.countries ? colegial?.countries.id : null}
                    disabled={true}
                    selectedCountry={areaCodeInput}
                    setSelectedCountry={(country) => {
                      queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                        ...old,
                        countrySelected: country?.id,
                        country: country?.id
                      }))
                      setAreaCodeInput(country)
                    }}
                    options={countryData?.body}
                  />
                </Box>
                {type === 'transportation' && (
                  <>
                    <PlacesAutocomplete
                      required={type === 'transportation'}
                      value={pickUp}
                      onChange={(value, placeId) => {
                        setPickUp(value)
                        queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                          ...old,
                          pickUp: value,
                          pickUpId: placeId
                        }))
                      }}
                      label={t('pickUp')}
                      helperText={t('pickUpLocation')}
                    />
                    <PlacesAutocomplete
                      required={type === 'transportation'}
                      value={dropOff}
                      onChange={(value, placeId) => {
                        setDropOff(value)
                        queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                          ...old,
                          dropOff: value,
                          dropOffId: placeId
                        }))
                      }}
                      label={t('dropOff')}
                      helperText={t('dropOffLocation')}
                    />
                    <Box
                      sx={{
                        gridColumn: '1 / -1',
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 2
                      }}
                    >
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                          queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                            ...old,
                            stops: [...(old.stops || []), { location: '', placeId: '' }]
                          }))
                        }}
                        variant="outlined"
                        size="small"
                      >
                        Agregar parada
                      </Button>
                    </Box>
                    {formData.stops &&
                      formData.stops.map((stop, index) => (
                        <Box
                          key={index}
                          sx={{
                            gridColumn: '1 / -1',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1
                          }}
                        >
                          <PlacesAutocomplete
                            required={false}
                            value={stop.location}
                            onChange={(value, placeId) => {
                              queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                                ...old,
                                stops: old.stops.map((s, i) =>
                                  i === index ? { location: value, placeId: placeId || '' } : s
                                )
                              }))
                            }}
                            label={`Parada ${index + 1}`}
                          />
                          <IconButton
                            onClick={() => {
                              queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                                ...old,
                                stops: old.stops.filter((_, i) => i !== index)
                              }))
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      ))}
                  </>
                )}
              </BoxStyled>
            )}
            <FancyInput
              className="my-3"
              multiline
              rows={2}
              label={t('generalObservations')}
              placeholder={type === 'transportation' ? t('generalObservationsTransportation') : ''}
              value={formData.generalObservations}
              onChange={(event) => {
                queryClient.setQueryData([setFormDataKey], (old: FormDataModel) => ({
                  ...old,
                  generalObservations: event.target.value
                }))
              }}
            />
            <TotalToPay
              amount={colegial.BookingAdmin ? 0 : cartData.allAmount}
              currency={currency}
            />
          </Stack>
          <StepsBtn
            onClickBack={formData.area === '' ? backBtn : backFromUser}
            onClickNext={handleNext}
            disabledNext={disabledNextBtn}
          />
        </Grid>
        <Grid
          item
          xs={12}
          lg={4}
          sx={{
            overflow: 'hidden',
            pt: '20px!important',
            pb: '15px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Stack
            sx={{
              maxHeight: {
                lg: '52vh'
              }
            }}
          >
            {formData.area &&
              (urlImg !== undefined && urlImg?.length > 2 ? (
                <Swiper
                  spaceBetween={10}
                  slidesPerView={1}
                  navigation
                  mousewheel={true}
                  direction={'horizontal'}
                  modules={[Navigation, Mousewheel]}
                  breakpoints={{
                    425: {
                      slidesPerView: 2,
                      direction: 'horizontal'
                    },
                    600: {
                      slidesPerView: 3,
                      direction: 'horizontal'
                    },
                    1201: {
                      slidesPerView: 3,
                      direction: 'vertical'
                    }
                  }}
                >
                  {urlImg?.map((img, i) => (
                    <SwiperSlide key={i}>
                      <img alt="name" src={img} onClick={() => handleLightbox(i)} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                urlImg?.map((img, i) => (
                  <div key={i}>
                    <img
                      alt="name"
                      height="175px"
                      src={img}
                      onClick={() => handleLightbox(i)}
                      className="img-without-slider"
                    />
                  </div>
                ))
              ))}
          </Stack>
        </Grid>
      </Grid>
      <LightboxStyled
        open={open}
        index={indexImg}
        close={() => setOpenLightbox(false)}
        slides={urlImgs}
        carousel={{ finite: true }}
        controller={{ closeOnBackdropClick: true }}
      />
    </>
  )
}

export default UserData
